import { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Fragment } from "react";
import { X, Ban } from "lucide-react";
import useTranslations from "@/shared/lang/useTranslations";

type CancelPaymentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading?: boolean;
};

export const CancelPaymentModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}: CancelPaymentModalProps) => {
  const { t } = useTranslations("payments");
  const [reason, setReason] = useState("");
  const [noteError, setNoteError] = useState<string | null>(null);

  const resetAndClose = useCallback(() => {
    setReason("");
    setNoteError(null);
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setNoteError(t("modal_cancel_required"));
      return;
    }
    setNoteError(null);
    onConfirm(trimmed);
    setReason("");
  }, [reason, onConfirm, t]);

  useEffect(() => {
    if (!isOpen) {
      setReason("");
      setNoteError(null);
    }
  }, [isOpen]);

  const handleReasonChange = useCallback((value: string) => {
    setReason(value);
    if (noteError) setNoteError(null);
  }, [noteError]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {}}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            onClick={resetAndClose}
            aria-hidden
          />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-[560px] rounded-lg shadow-2xl overflow-hidden border border-gray-100 dark:border-zinc-800 bg-white dark:bg-zinc-900 transform transition-all">
                <div className="flex items-center justify-between px-8 pt-8 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-rose-500/10">
                      <Ban className="h-6 w-6 text-rose-500 dark:text-rose-400" />
                    </div>
                    <h2 className="text-gray-900 dark:text-white text-2xl font-bold tracking-tight">
                      {t("modal_cancel_title")}
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={resetAndClose}
                    disabled={isLoading}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors p-1"
                    aria-label={t("modal_cancel_close_aria")}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="px-8 py-2">
                  <p className="text-gray-600 dark:text-zinc-400 text-base leading-relaxed mb-6">
                    {t("modal_cancel_description")}
                  </p>
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="motivo-cancelacion-pago"
                      className="text-sm font-semibold text-gray-700 dark:text-zinc-300 px-1"
                    >
                      {t("modal_cancel_label_reason")}
                    </label>
                    <textarea
                      id="motivo-cancelacion-pago"
                      value={reason}
                      onChange={(e) => handleReasonChange(e.target.value)}
                      placeholder={t("modal_cancel_placeholder")}
                      disabled={isLoading}
                      rows={5}
                      className={`w-full min-h-[160px] resize-none rounded-xl border bg-gray-50 dark:bg-zinc-800/50 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-rose-500/20 p-4 text-base transition-all ${
                        noteError
                          ? "border-rose-500 dark:border-rose-500 focus:border-rose-500"
                          : "border-gray-200 dark:border-zinc-600 focus:border-rose-500"
                      }`}
                      aria-invalid={!!noteError}
                      aria-describedby={noteError ? "motivo-cancelacion-pago-error" : undefined}
                    />
                    {noteError && (
                      <p
                        id="motivo-cancelacion-pago-error"
                        className="text-sm text-rose-600 dark:text-rose-400 px-1"
                        role="alert"
                      >
                        {noteError}
                      </p>
                    )}
                  </div>
                </div>

                <div className="px-8 py-8 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={resetAndClose}
                    disabled={isLoading}
                    className="min-w-[100px] h-12 px-6 rounded-full bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-white text-base font-bold transition-all hover:bg-gray-200 dark:hover:bg-zinc-700 disabled:opacity-50"
                  >
                    {t("modal_cancel_back")}
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={isLoading}
                    className="min-w-[180px] h-12 px-8 rounded-full bg-rose-500 text-white text-base font-bold transition-all hover:brightness-110 shadow-lg shadow-rose-500/20 disabled:opacity-50"
                  >
                    {isLoading ? t("modal_cancel_confirming") : t("modal_cancel_confirm")}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
